import { BaseCard2 } from "@/components/shared/card/base-card-2.0";
import { StatCard } from "@/components/shared/card/stat-card";
import { ChessBoard } from "@/components/shared/chess-board";
import { PageCardContainer } from "@/components/shared/page-card/page-card-container";
import { RecentGamesList } from "@/components/shared/recent-games-list";
import { Button } from "@/components/ui/button";
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from "@/components/ui/card";
import { getPageSummary, PAGE } from "@/pages";
import { useLoading } from "@/providers/global-loading-provider";
import { ToastService } from "@/services/toast-service";
import { PresentationUtil } from "@/utils/presentation-util";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { capitalize, isEmpty } from "@banjoanton/utils";
import {
    ChessComService,
    ChessGameCollectionRepository,
    CollectionStatsRepository,
    STATS_VERSION,
    StatsService,
} from "@chesspresso/chess";
import { ChessGameRepository } from "@chesspresso/chess/src/db/chess-game-repository";
import { createLogger } from "@chesspresso/common";
import {
    createFileRoute,
    useNavigate,
    useRouter,
} from "@tanstack/react-router";
import { Crown, Star, Swords, Target, Trophy, Zap } from "lucide-react";
import { type PropsWithChildren, useState } from "react";

const logger = createLogger("overview-route");

export const Route = createFileRoute("/(container)/overview")({
    component: RouteComponent,
    loader: async () => {
        const games = await ChessGameRepository.getAll();
        const collections = await ChessGameCollectionRepository.getAll();

        const returnData = {
            games,
            collections,
        };

        if (!games || games.length === 0) {
            return { ...returnData, stats: undefined };
        }

        const collectionId = games.find((g) => g.collectionId)?.collectionId;

        if (!collectionId) {
            return { ...returnData, stats: undefined };
        }

        const cached = await CollectionStatsRepository.get(collectionId);
        const gameCount =
            await ChessGameRepository.countByCollection(collectionId);

        if (
            cached &&
            cached.version === STATS_VERSION &&
            cached.totalGames === gameCount
        ) {
            return { games, stats: cached, collections };
        }

        // Fallback: compute, cache, and return
        const stats = StatsService.calculateStats(games, collectionId);
        await CollectionStatsRepository.save(stats);

        return { games, stats, collections };
    },
});

const page = getPageSummary(PAGE.Overview);

type PageCardWrapperProps = PropsWithChildren<{
    onFetchLatestGames?: () => void;
}>;

const PageCardWrapper = ({
    children,
    onFetchLatestGames,
}: PageCardWrapperProps) => (
    <PageCardContainer
        title={page.title}
        Icon={page.icon}
        description="Review your chess performance and recent games"
        headerActions={
            onFetchLatestGames ? (
                <Button
                    className="cursor-pointer"
                    variant="outline"
                    onClick={onFetchLatestGames}
                >
                    Fetch games
                </Button>
            ) : null
        }
    >
        {children}
    </PageCardContainer>
);

function RouteComponent() {
    const { games, stats, collections } = Route.useLoaderData();
    const { setLoading } = useLoading();

    const sorted = games.toSorted((a, b) => b.endTimeUnix - a.endTimeUnix);
    const recentGames = sorted.slice(0, 9);
    const mostRecentGame = recentGames[0];

    const timeClasses = stats?.statsByType.map((s) => s.type) ?? [];
    const [selectedType, setSelectedType] = useState(
        timeClasses.includes("rapid") ? "rapid" : (timeClasses[0] ?? "rapid")
    );
    const statsToShow = stats?.statsByType.find((s) => s.type === selectedType);

    const navigate = useNavigate();
    const router = useRouter();

    const handleCurrentGameClick = () => {
        if (!mostRecentGame) {
            return;
        }
        navigate({
            to: "/analyze/$slug",
            params: { slug: mostRecentGame.slug },
        });
    };
    const handleRecentGamesClick = () => navigate({ to: "/games" });

    const handleFetchLatestGames = async () => {
        if (!mostRecentGame) {
            logger.warn("No most recent game found, cannot fetch latest games");
            return;
        }

        setLoading({
            text: "Fetching latest games...",
            isLoading: true,
            progress: 0,
        });
        const addedGamesResult = await ChessComService.fetchAndSaveLatestGames({
            player: mostRecentGame.player.username,
            mostRecentGame,
            collectionId: mostRecentGame.collectionId,
            onProgress: (progress) => {
                setLoading({
                    progress,
                    isLoading: true,
                });
            },
        });

        setLoading({ isLoading: false });

        if (!addedGamesResult.ok) {
            ToastService.error(
                `Failed to fetch latest games: ${addedGamesResult.message}`
            );
            return;
        }

        if (addedGamesResult.data.length === 0) {
            ToastService.success("No new games found");
            return;
        }

        ToastService.success(`Added ${addedGamesResult.data.length} new games`);
        router.invalidate();
    };

    if (isEmpty(collections)) {
        return (
            <PageCardWrapper>
                <Card>
                    <CardHeader className="items-center">
                        <CardTitle className="text-center">
                            No games yet
                        </CardTitle>
                        <CardDescription className="text-center mb-4">
                            You haven't imported any chess games.
                            <br />
                            Start by importing your games to see them here.
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="flex justify-center">
                        <Button
                            type="button"
                            variant="default"
                            className="cursor-pointer"
                            onClick={() => navigate({ to: "/import" })}
                        >
                            Import games
                        </Button>
                    </CardContent>
                </Card>
            </PageCardWrapper>
        );
    }

    return (
        <PageCardWrapper onFetchLatestGames={handleFetchLatestGames}>
            {statsToShow ? (
                <>
                    {timeClasses.length > 1 ? (
                        <Tabs
                            value={selectedType}
                            onValueChange={setSelectedType}
                            className="mb-4"
                        >
                            <TabsList>
                                {timeClasses.map((type) => (
                                    <TabsTrigger key={type} value={type}>
                                        {capitalize(type)}
                                    </TabsTrigger>
                                ))}
                            </TabsList>
                        </Tabs>
                    ) : null}
                    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 mb-6">
                        <StatCard
                            title="Current Rating"
                            value={statsToShow.currentRating.toString()}
                            description={capitalize(statsToShow.type)}
                            icon={Trophy}
                        />
                        <StatCard
                            title="Highest Rating"
                            value={statsToShow.highestRating.toString()}
                            description={capitalize(statsToShow.type)}
                            icon={Star}
                        />
                        <StatCard
                            title="Accuracy"
                            value={PresentationUtil.toPercentage(
                                statsToShow.totalPerformance.accuracy
                                    .totalAccuracy
                            )}
                            description={capitalize(statsToShow.type)}
                            icon={Target}
                            progress={
                                statsToShow.totalPerformance.accuracy
                                    .totalAccuracy * 100
                            }
                        />
                        <StatCard
                            title="Win Rate"
                            value={PresentationUtil.toPercentage(
                                statsToShow.totalPerformance.winPercentage
                            )}
                            description={capitalize(statsToShow.type)}
                            icon={Crown}
                            progress={
                                statsToShow.totalPerformance.winPercentage * 100
                            }
                        />
                        <StatCard
                            title="W / L / D"
                            value={`${statsToShow.totalPerformance.wins} / ${statsToShow.totalPerformance.losses} / ${statsToShow.totalPerformance.draws}`}
                            description={capitalize(statsToShow.type)}
                            icon={Swords}
                        />
                        <StatCard
                            title="Games Played"
                            value={statsToShow.totalGames.toString()}
                            description={capitalize(statsToShow.type)}
                            icon={Zap}
                        />
                    </div>
                </>
            ) : null}
            <div className="flex flex-col lg:flex-row lg:h-[35rem] xl:h-[40rem] 2xl:h-[55rem] gap-4 overflow-auto">
                <BaseCard2
                    title="Current Game"
                    description="Analyze your most recent game"
                    buttonLabel="Analyze"
                    buttonOnClick={handleCurrentGameClick}
                    className="w-full lg:w-1/2 h-full"
                >
                    {mostRecentGame ? (
                        <ChessBoard game={mostRecentGame} />
                    ) : null}
                </BaseCard2>

                <BaseCard2
                    title="Recent Games"
                    description="Explore all your games"
                    buttonLabel="View all"
                    buttonOnClick={handleRecentGamesClick}
                    className="w-full lg:w-1/2 h-full"
                >
                    <RecentGamesList games={recentGames} />
                </BaseCard2>
            </div>
        </PageCardWrapper>
    );
}
